import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, AlertTriangle, CheckCircle, XCircle, ChevronLeft, 
  ChevronRight, Lock, Eye, EyeOff, User, Mail, Users, FileText
} from 'lucide-react';
import Badge from '@/components/Badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FinalExamPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  
  // Exam state
  const [examState, setExamState] = useState('proctorInfo'); // proctorInfo, instructions, exam, submitted
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Proctor information
  const [proctorName, setProctorName] = useState('');
  const [proctorEmail, setProctorEmail] = useState('');
  const [proctorRelationship, setProctorRelationship] = useState('');
  const [attestation, setAttestation] = useState(false);
  
  // Exam data
  const [examData, setExamData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [examStartTime, setExamStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(180 * 60); // 3 hours in seconds
  
  // Results
  const [results, setResults] = useState(null);
  
  // Timer ref
  const timerRef = useRef(null);

  useEffect(() => {
    loadExam();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Timer effect
  useEffect(() => {
    if (examState === 'exam' && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmit(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [examState]);

  const loadExam = async () => {
    try {
      const response = await axios.get(`${API}/quizzes/proctored-final-exam`);
      setExamData(response.data);
      setQuestions(response.data.questions);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load exam:', error);
      if (error.response?.status === 403) {
        toast.error('You must complete all chapters before taking the final exam');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load exam');
      }
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProctorSubmit = () => {
    if (!proctorName.trim() || !proctorEmail.trim() || !proctorRelationship.trim()) {
      toast.error('Please fill in all proctor information');
      return;
    }
    if (!attestation) {
      toast.error('Please confirm the attestation checkbox');
      return;
    }
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(proctorEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setExamState('instructions');
  };

  const startExam = () => {
    setExamStartTime(Date.now());
    setExamState('exam');
  };

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleSubmit = async (timeExpired = false) => {
    if (!timeExpired) {
      const unanswered = questions.filter(q => !answers[q.id]).length;
      if (unanswered > 0) {
        const confirm = window.confirm(`You have ${unanswered} unanswered questions. Are you sure you want to submit?`);
        if (!confirm) return;
      }
    }

    setSubmitting(true);
    const timeTaken = Math.floor((Date.now() - examStartTime) / 1000);

    try {
      const response = await axios.post(`${API}/quizzes/proctored-final-exam/submit`, {
        answers,
        time_taken_seconds: timeTaken,
        proctor_name: proctorName,
        proctor_email: proctorEmail,
        proctor_relationship: proctorRelationship
      });
      
      setResults(response.data);
      setExamState('submitted');
      
      if (response.data.passed) {
        toast.success('Congratulations! You passed the final exam!');
      } else {
        toast.error('You did not pass. Review the material and try again.');
      }
    } catch (error) {
      console.error('Failed to submit exam:', error);
      toast.error('Failed to submit exam. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading Final Exam...</div>
      </div>
    );
  }

  // Proctor Information Screen
  if (examState === 'proctorInfo') {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ChevronLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-12">
          <div className="bg-card border border-border rounded-sm p-8">
            <div className="text-center mb-8">
              <Badge className="w-16 h-16 mx-auto mb-4" />
              <h1 className="text-3xl font-black mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                FINAL CERTIFICATION EXAM
              </h1>
              <p className="text-muted-foreground">
                150 Questions • 3 Hour Time Limit • 75% Passing Score
              </p>
            </div>

            <div className="bg-accent/10 border border-accent/30 rounded-sm p-4 mb-8">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-accent mt-0.5" />
                <div>
                  <h3 className="font-semibold text-accent mb-1">Proctor Required</h3>
                  <p className="text-sm text-muted-foreground">
                    This exam requires a proctor to verify your identity and ensure exam integrity. 
                    Your proctor will receive an email confirmation and may be contacted for verification.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <Label htmlFor="proctorName" className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4" />
                  Proctor Full Name *
                </Label>
                <Input
                  id="proctorName"
                  value={proctorName}
                  onChange={(e) => setProctorName(e.target.value)}
                  placeholder="Enter proctor's full legal name"
                  data-testid="proctor-name-input"
                />
              </div>

              <div>
                <Label htmlFor="proctorEmail" className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4" />
                  Proctor Email Address *
                </Label>
                <Input
                  id="proctorEmail"
                  type="email"
                  value={proctorEmail}
                  onChange={(e) => setProctorEmail(e.target.value)}
                  placeholder="Enter proctor's email address"
                  data-testid="proctor-email-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Proctor will receive verification email after exam submission
                </p>
              </div>

              <div>
                <Label htmlFor="proctorRelationship" className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4" />
                  Relationship to Candidate *
                </Label>
                <Input
                  id="proctorRelationship"
                  value={proctorRelationship}
                  onChange={(e) => setProctorRelationship(e.target.value)}
                  placeholder="e.g., Employer, Supervisor, Colleague, Family Member"
                  data-testid="proctor-relationship-input"
                />
              </div>

              <div className="bg-secondary/50 rounded-sm p-4">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="attestation"
                    checked={attestation}
                    onCheckedChange={setAttestation}
                    data-testid="attestation-checkbox"
                  />
                  <Label htmlFor="attestation" className="text-sm cursor-pointer">
                    I attest that the proctor named above will be physically present during my exam, 
                    will verify my identity, and will ensure I complete this exam without unauthorized 
                    assistance or materials. I understand that falsifying proctor information may result 
                    in exam invalidation and course disqualification.
                  </Label>
                </div>
              </div>

              <Button 
                className="w-full"
                onClick={handleProctorSubmit}
                disabled={!proctorName || !proctorEmail || !proctorRelationship || !attestation}
                data-testid="continue-to-instructions-btn"
              >
                Continue to Exam Instructions
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Instructions Screen
  if (examState === 'instructions') {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setExamState('proctorInfo')} data-testid="back-to-proctor">
              <ChevronLeft className="w-5 h-5 mr-2" />
              Back to Proctor Info
            </Button>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-12">
          <div className="bg-card border border-border rounded-sm p-8">
            <div className="text-center mb-8">
              <FileText className="w-16 h-16 text-primary mx-auto mb-4" />
              <h1 className="text-3xl font-black mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                EXAM INSTRUCTIONS
              </h1>
            </div>

            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4 p-4 bg-secondary/50 rounded-sm">
                <Clock className="w-6 h-6 text-primary mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">Time Limit</h3>
                  <p className="text-sm text-muted-foreground">
                    You have <strong>3 hours</strong> to complete all 150 questions. The exam will 
                    automatically submit when time expires.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-secondary/50 rounded-sm">
                <Lock className="w-6 h-6 text-accent mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">No Going Back</h3>
                  <p className="text-sm text-muted-foreground">
                    Once you answer a question and move to the next, you <strong>cannot go back</strong>. 
                    Make sure you are confident in your answer before proceeding.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-secondary/50 rounded-sm">
                <AlertTriangle className="w-6 h-6 text-destructive mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">Randomized Questions & Answers</h3>
                  <p className="text-sm text-muted-foreground">
                    Questions appear in random order. Answer choices are also randomized. 
                    Each exam is unique.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-green-500/10 rounded-sm">
                <CheckCircle className="w-6 h-6 text-green-500 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">Passing Score</h3>
                  <p className="text-sm text-muted-foreground">
                    You must score <strong>75% or higher</strong> (113 out of 150 correct) to pass.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-primary/10 border border-primary/30 rounded-sm p-4 mb-8">
              <p className="text-sm">
                <strong>Proctor:</strong> {proctorName} ({proctorEmail})
              </p>
            </div>

            <Button 
              className="w-full bg-primary hover:bg-primary/90"
              size="lg"
              onClick={startExam}
              data-testid="start-exam-btn"
            >
              START EXAM
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Exam Screen
  if (examState === 'exam') {
    const currentQuestion = questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
    const isLastQuestion = currentQuestionIndex === questions.length - 1;
    const currentAnswer = answers[currentQuestion?.id];

    return (
      <div className="min-h-screen bg-background">
        {/* Fixed Header */}
        <header className="border-b border-border bg-card fixed top-0 left-0 right-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge className="w-6 h-6" />
              <span className="font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                FINAL EXAM
              </span>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-sm">
                Question <strong>{currentQuestionIndex + 1}</strong> of <strong>{questions.length}</strong>
              </div>
              
              <div className={`flex items-center gap-2 px-3 py-1 rounded-sm ${
                timeRemaining < 300 ? 'bg-destructive/20 text-destructive' : 
                timeRemaining < 900 ? 'bg-accent/20 text-accent' : 'bg-secondary'
              }`}>
                <Clock className="w-4 h-4" />
                <span className="font-mono font-bold">{formatTime(timeRemaining)}</span>
              </div>
            </div>
          </div>
          <Progress value={progress} className="h-1" />
        </header>

        {/* Question Content */}
        <main className="pt-24 pb-32 px-6">
          <div className="max-w-3xl mx-auto">
            {currentQuestion && (
              <div className="bg-card border border-border rounded-sm p-8">
                <div className="mb-2 text-sm text-muted-foreground">
                  {currentQuestion.options.length === 2 ? 'True/False' : 'Multiple Choice'}
                </div>
                
                <h2 className="text-xl font-semibold mb-6 leading-relaxed">
                  {currentQuestion.question_text}
                </h2>

                <div className="space-y-3">
                  {currentQuestion.options.map((option, index) => {
                    const letter = String.fromCharCode(65 + index);
                    const isSelected = currentAnswer === letter;
                    
                    return (
                      <button
                        key={index}
                        onClick={() => handleAnswerSelect(currentQuestion.id, letter)}
                        className={`w-full text-left p-4 rounded-sm border transition-all ${
                          isSelected 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:border-primary/50 hover:bg-secondary/50'
                        }`}
                        data-testid={`answer-option-${letter}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            isSelected ? 'bg-primary text-primary-foreground' : 'bg-secondary'
                          }`}>
                            {letter}
                          </span>
                          <span className="flex-1 pt-1">{option}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Fixed Footer */}
        <footer className="border-t border-border bg-card fixed bottom-0 left-0 right-0">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {Object.keys(answers).length} of {questions.length} answered
            </div>
            
            {isLastQuestion ? (
              <Button 
                onClick={() => handleSubmit(false)}
                disabled={!currentAnswer || submitting}
                className="bg-green-600 hover:bg-green-700"
                data-testid="submit-exam-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Exam'}
              </Button>
            ) : (
              <Button 
                onClick={goToNextQuestion}
                disabled={!currentAnswer}
                data-testid="next-question-btn"
              >
                Next Question
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </footer>
      </div>
    );
  }

  // Results Screen
  if (examState === 'submitted' && results) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
              EXAM RESULTS
            </h1>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-12">
          <div className="bg-card border border-border rounded-sm p-8 text-center">
            {results.passed ? (
              <>
                <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                <h2 className="text-3xl font-black text-green-500 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  CONGRATULATIONS!
                </h2>
                <p className="text-xl mb-6">You passed the final exam!</p>
              </>
            ) : (
              <>
                <XCircle className="w-20 h-20 text-destructive mx-auto mb-4" />
                <h2 className="text-3xl font-black text-destructive mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  NOT PASSED
                </h2>
                <p className="text-xl mb-6">Review the course material and try again.</p>
              </>
            )}

            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-secondary rounded-sm p-4">
                <div className="text-3xl font-black" style={{ fontFamily: 'Barlow Condensed' }}>
                  {results.score}
                </div>
                <div className="text-sm text-muted-foreground">Correct</div>
              </div>
              <div className="bg-secondary rounded-sm p-4">
                <div className="text-3xl font-black" style={{ fontFamily: 'Barlow Condensed' }}>
                  {results.total}
                </div>
                <div className="text-sm text-muted-foreground">Total</div>
              </div>
              <div className="bg-secondary rounded-sm p-4">
                <div className={`text-3xl font-black ${results.passed ? 'text-green-500' : 'text-destructive'}`} style={{ fontFamily: 'Barlow Condensed' }}>
                  {results.percentage}%
                </div>
                <div className="text-sm text-muted-foreground">Score</div>
              </div>
            </div>

            <div className="text-sm text-muted-foreground mb-6">
              Passing Score: 75% (113/150)
            </div>

            {results.passed && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-4 mb-6">
                <p className="text-sm">
                  Your proctor ({results.proctor_name}) will receive an email confirmation. 
                  Your certificate is pending review.
                </p>
              </div>
            )}

            <Button onClick={() => navigate('/dashboard')} data-testid="return-dashboard-btn">
              Return to Dashboard
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return null;
};

export default FinalExamPage;
